import { Platform } from '@microsoft/signalr/dist/esm/Utils';
import {
  APP_CONNECTIVITY_STATE_ENUM,
  APP_TYPE_ENUM,
  PLATFORM_ENUM,
} from '@tenant/utility/constants/enum';
import { PRICING_TIER } from 'constants/Pricing';
import * as Yup from 'yup';

export default [
  Yup.object().shape({
    Name: Yup.string().max(255).required('Name app is required'),
    CategoryIds: Yup.array().nullable().min(1).required().label('Categories'),
    Personas: Yup.array().nullable().label('Personas'),
    KeyLabels: Yup.string().max(255).nullable().label('Key Labels'),
    Dependencies: Yup.string().max(255).nullable().label('Dependencies'),
    AuthorName: Yup.string().max(255).required().label('Author Name'),
    AuthorEmail: Yup.string().max(255).required().label('Author Email'),
    ReferralFormUrl: Yup.string().nullable().max(255).url('Enter a valid URL'),
    IconBlobReference: Yup.mixed()
      .test(
        'fileSize',
        'File size is too large, must be less than 2MB',
        (value) => {
          if (
            typeof value === 'string' ||
            typeof value === 'undefined' ||
            !value
          ) {
            return true;
          }

          return value && value.size <= 2 * 1024 * 1024; // 2MB limit
        },
      )
      .test(
        'fileType',
        'Unsupported file type, only images are allowed',
        (value) => {
          if (
            typeof value === 'string' ||
            typeof value === 'undefined' ||
            !value
          ) {
            return true;
          }
          return value && value.type.startsWith('image/');
        },
      )
      .label('Icon'),
  }),
  Yup.object().shape({
    ShortDescription: Yup.string()
      .max(1024)
      .nullable()
      .label('Short Description'),
    LongDescription: Yup.string()
      .max(1073741823)
      .nullable()
      .label('Long Description'),
    MetaTitle: Yup.string().max(255).nullable().label('Meta Title'),
    MetaDescription: Yup.string()
      .max(1000)
      .nullable()
      .label('Meta Description'),
  }),
  Yup.object().shape({
    Platform: Yup.number().required().label('Platform'),
    ModuleModel: Yup.object()
      .nullable()
      .when(['IntegrationMode'], {
        is: (integrationMode) => {
          const isModule = integrationMode === APP_TYPE_ENUM['Modular'];
          return isModule;
        },
        then: Yup.object().nullable().required().label('Module'),
        otherwise: Yup.object().nullable().label('Module'),
      }),
    LaunchType: Yup.number().required().label('LaunchType'),
    AppCode: Yup.string()
      .max(255)
      .nullable()
      .when(['Platform'], {
        is: (Platform) => {
          const isSupportMobile =
            Platform === PLATFORM_ENUM['Web'] ||
            Platform === PLATFORM_ENUM['Hybrid'];
          return isSupportMobile;
        },
        then: Yup.string()
          .matches(
            /^[{]?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}[}]?$/,
            'Invalid Web App Code format',
          )
          .nullable()
          .required()
          .label('Web App Code'),
        otherwise: Yup.string().nullable(),
      }),
    //For Mobile
    IntegrationMode: Yup.number()
      .when(['Platform'], {
        is: (Platform) => {
          const isSupportWeb =
            Platform === PLATFORM_ENUM['Web'] ||
            Platform === PLATFORM_ENUM['Hybrid'];
          return isSupportWeb;
        },
        then: Yup.number()
          .nullable()
          .required('Web Integration Mode is required'),
        otherwise: Yup.number().nullable(),
      })
      .label('Web Integration Mode'),
    MobileIntegrationMode: Yup.number().when(['Platform'], {
      is: (Platform) => {
        const isSupportMobile =
          Platform === PLATFORM_ENUM['Mobile'] ||
          Platform === PLATFORM_ENUM['Hybrid'];
        return isSupportMobile;
      },
      then: Yup.number().required('Mobile Integration Mode is required'),
      otherwise: Yup.number().nullable(),
    }),
    AppConnectivityState: Yup.number().when(
      ['Platform', 'MobileIntegrationMode'],
      {
        is: (Platform, MobileIntegrationMode) => {
          const isSupportMobile =
            Platform === PLATFORM_ENUM['Mobile'] ||
            Platform === PLATFORM_ENUM['Hybrid'];
          const shouldRequiredIntegrationMode =
            MobileIntegrationMode !== APP_TYPE_ENUM['Modular'];
          return isSupportMobile && shouldRequiredIntegrationMode;
        },
        then: Yup.number()
          .nullable()
          .required('Connectivity State is required'),
        otherwise: Yup.number().nullable(),
      },
    ),
    MobileAppCode: Yup.string()
      .max(255)
      .nullable()
      .when(['Platform'], {
        is: (Platform) => {
          const isSupportMobile =
            Platform === PLATFORM_ENUM['Mobile'] ||
            Platform === PLATFORM_ENUM['Hybrid'];
          return isSupportMobile;
        },
        then: Yup.string()
          .matches(
            /^[{]?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}[}]?$/,
            'Invalid Mobile App Code format',
          )
          .nullable()
          .required('Mobile App Code is required'),
        otherwise: Yup.string().nullable(),
      }),
    BundleZipBlobReference: Yup.string().when(
      ['Platform', 'AppConnectivityState', 'MobileAppUrl'],
      {
        is: (Platform, AppConnectivityState, MobileAppUrl) => {
          const isSupportMobile =
            Platform === PLATFORM_ENUM['Mobile'] ||
            Platform === PLATFORM_ENUM['Hybrid'];
          const isSupportOffline =
            AppConnectivityState === APP_CONNECTIVITY_STATE_ENUM['Offline'];

          if (isSupportOffline && MobileAppUrl) {
            return false;
          }
          return isSupportMobile && isSupportOffline;
        },
        then: Yup.string().required('Bundle zip file is required'),
        otherwise: Yup.string(),
      },
    ),
    MobileFeatureUrl: Yup.string()
      .max(255)
      .when(['Platform', 'AppConnectivityState'], {
        is: (Platform, AppConnectivityState) => {
          const isSupportMobile =
            Platform === PLATFORM_ENUM['Mobile'] ||
            Platform === PLATFORM_ENUM['Hybrid'];
          const isSupportOnline =
            AppConnectivityState === APP_CONNECTIVITY_STATE_ENUM['Online'];
          return isSupportMobile && isSupportOnline;
        },
        then: Yup.string().required('Hosted Mobile App Address is required'),
        otherwise: Yup.string(),
      }),
  }),
  Yup.object().shape({
    IsMultiTierEnabled: Yup.boolean(),
    PricingPlan: Yup.array().when('IsMultiTierEnabled', {
      is: (IsMultiTierEnabled) => {
        return !IsMultiTierEnabled;
      },

      then: Yup.array().of(
        Yup.object().shape({
          // Features: Yup.string()
          //   .nullable()
          //   .when('Tier', {
          //     is: (tier) => tier === PRICING_TIER.Basic,
          //     then: Yup.string()
          //       .nullable()
          //       .required('Key Features are required')
          //       .test('has-features', 'Key Features are required', function (value) {
          //         return value && value.trim().length > 0;
          //       }),
          //     otherwise: Yup.string().nullable().notRequired(),
          //   }),
          Plans: Yup.array().when('Tier', {
            is: (tier) => tier === PRICING_TIER.Basic,
            then: Yup.array()
              .of(
                Yup.object().shape({
                  BillingCycle: Yup.number(),
                  IsActive: Yup.boolean().notRequired(),
                  Price: Yup.number().when(['BillingCycle', 'IsActive'], {
                    is: (billingCycle, isActive) => {
                      if (isActive === false) {
                        return false;
                      }

                      return (isActive === true || isActive === undefined) &&
                        billingCycle > 0
                    },
                    then: Yup.number()
                      .transform((value) => (isNaN(value) ? undefined : value))
                      .typeError('Price must be a number')
                      .required('Price is required')
                      .min(0.01, 'Price must be greater than 0'),
                    otherwise: Yup.number().notRequired(),
                  }),
                }),
              )
              .min(1, 'At least one billing frequency is required'),
            otherwise: Yup.array().notRequired(),
          }),
        }),
      ),

      otherwise: Yup.array().of(
        Yup.object().shape({
          // Features: Yup.string()
          //   .nullable()
          //   .when('Tier', {
          //     is: (tier) => true,
          //     then: Yup.string()
          //       .nullable()
          //       .required('Key Features are required')
          //       .test('has-features', 'Key Features are required', function (value) {
          //         return value && value.trim().length > 0;
          //       }),
          //     otherwise: Yup.string().nullable().notRequired(),
          //   }),
          Plans: Yup.array().when('Tier', {
            is: (tier) => tier === PRICING_TIER.Basic,
            then: Yup.array()
              .of(
                Yup.object().shape({
                  BillingCycle: Yup.number(),
                  IsActive: Yup.boolean().notRequired(),
                  Price: Yup.number().when(['BillingCycle', 'IsActive'], {
                    is: (billingCycle, isActive) => {
                      if (isActive === false) {
                        return false;
                      }

                      return (isActive === true || isActive === undefined) &&
                        billingCycle > 0
                    },
                    then: Yup.number()
                      .transform((value) => (isNaN(value) ? undefined : value))
                      .typeError('Price must be a number')
                      .required('Price is required')
                      .min(0.01, 'Price must be greater than 0'),
                    otherwise: Yup.number().notRequired(),
                  }),
                }),
              )
              .min(1, 'At least one billing frequency is required'),
            otherwise: Yup.array().of(
              Yup.object().shape({
                BillingCycle: Yup.number(),
                IsActive: Yup.boolean().notRequired(),
                Price: Yup.number().when(['BillingCycle', 'IsActive'], {
                  is: (billingCycle, isActive) => {
                    if (isActive === false) {
                      return false;
                    }

                    return (isActive === true || isActive === undefined) &&
                      billingCycle > 0
                  },
                  then: Yup.number()
                    .transform((value) => (isNaN(value) ? undefined : value))
                    .typeError('Price must be a number')
                    .required('Price is required')
                    .min(0.01, 'Price must be greater than 0'),
                  otherwise: Yup.number().notRequired(),
                }),
              }),
            ),
          }),
        }),
      ),
    }),
  }),
];
